import { useEffect, useState, useCallback } from 'react';
import { mgmt, KcRole } from '../services/api';
import { errMsg, splitList } from '../utils';

export default function RolesPage() {
  const [roles, setRoles] = useState<KcRole[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [composites, setComposites] = useState<KcRole[]>([]);
  const [err, setErr] = useState('');

  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [addNames, setAddNames] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await mgmt.listRoles();
      setRoles(r.data.roles);
    } catch (e) {
      setErr(errMsg(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openRole = async (name: string) => {
    setSelected(name);
    setErr('');
    try {
      const r = await mgmt.getRole(name);
      setComposites(r.data.composites);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const createRole = async () => {
    if (!newName) return;
    try {
      await mgmt.createRole({ name: newName, description: newDesc || undefined });
      setNewName('');
      setNewDesc('');
      load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const addComposites = async () => {
    if (!selected) return;
    try {
      await mgmt.addComposites(selected, splitList(addNames));
      setAddNames('');
      openRole(selected);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const removeComposite = async (name: string) => {
    if (!selected) return;
    try {
      await mgmt.removeComposites(selected, [name]);
      openRole(selected);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Rollen &amp; Berechtigungen</h2>
      <p className="text-sm text-gray-400">
        Einzelne Rollen sind Berechtigungen; Composite-Rollen bündeln mehrere Berechtigungen.
      </p>
      {err && <p className="text-coral">{err}</p>}

      <div className="grid grid-cols-2 gap-6">
        <section className="card space-y-3">
          <h3 className="font-medium text-coral">Rollen</h3>
          <ul className="text-sm">
            {roles.map((r) => (
              <li key={r.id}>
                <button
                  className={`block w-full border-t border-edge py-1.5 text-left ${
                    selected === r.name ? 'text-coral' : 'text-gray-200'
                  }`}
                  onClick={() => openRole(r.name)}
                >
                  {r.name}
                  {r.description && <span className="text-gray-500"> — {r.description}</span>}
                </button>
              </li>
            ))}
          </ul>
          <div className="space-y-2 border-t border-edge pt-3">
            <label className="label">Neue Rolle</label>
            <input
              className="input"
              placeholder="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <input
              className="input"
              placeholder="Beschreibung (optional)"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
            <button className="btn-primary" disabled={!newName} onClick={createRole}>
              Rolle anlegen
            </button>
          </div>
        </section>

        <section className="card space-y-3">
          {selected ? (
            <>
              <h3 className="font-medium text-coral">Berechtigungs-Bündel: {selected}</h3>
              <div className="flex flex-wrap gap-1">
                {composites.length === 0 && (
                  <span className="text-gray-500">keine gebündelten Rollen</span>
                )}
                {composites.map((c) => (
                  <span key={c.id} className="badge bg-edge text-gray-300">
                    {c.name}
                    <button
                      className="ml-1 text-coral/70 hover:text-coral"
                      onClick={() => removeComposite(c.name)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              <div>
                <label className="label">Rollen bündeln (Komma)</label>
                <input
                  className="input"
                  value={addNames}
                  onChange={(e) => setAddNames(e.target.value)}
                />
              </div>
              <button className="btn-primary" disabled={!addNames} onClick={addComposites}>
                Hinzufügen
              </button>
            </>
          ) : (
            <p className="text-gray-500">Rolle auswählen, um Berechtigungen zu bündeln.</p>
          )}
        </section>
      </div>
    </div>
  );
}
